import { Box, Typography } from "@mui/material"
import type { FC } from "react"
import { useCallback, useRef } from "react"
import { useReactToPrint } from "react-to-print"

import { PRINT_PAGE_STYLE } from "./print.config"

import type { ButtonProps } from "@/components/common/Button"
import { Button } from "@/components/common/Button"
import { DATE_FORMAT } from "@/constants/formats"
import type { GoodsReceipt, GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { PutAwayOrderPalletsTable } from "@/screens/WarehouseReceiving/components/GoodsReceiptPalletsTable"
import { AddressFormat } from "@/types/common"
import { formatDate } from "@/utils/date"
import { formatLocationAddress } from "@/utils/utils"

interface PrintPutAwayOrderButtonProps {
  goodsReceipt?: GoodsReceipt
  buttonProps: ButtonProps & { "data-testid"?: string }
  buttonLabel: string
  pallets: GoodsReceiptPallet[]
}

export const PrintPutAwayOrderButton: FC<PrintPutAwayOrderButtonProps> = ({
  goodsReceipt,
  buttonProps,
  buttonLabel,
  pallets,
}) => {
  const printedComponentRef = useRef<HTMLDivElement>(null)

  const handlePrint = useReactToPrint({
    contentRef: printedComponentRef,
    documentTitle: "Put Away Order",
    bodyClass: "root",
    preserveAfterPrint: true,
    print: async (printIframe) => {
      printIframe.style.display = "none"
      printIframe.contentWindow?.print()
    },
    pageStyle: PRINT_PAGE_STYLE,
  })

  const printPutAwayOrder = useCallback(() => {
    if (!goodsReceipt || !pallets) return
    handlePrint()
  }, [handlePrint, goodsReceipt, pallets])

  return (
    <>
      <Button {...buttonProps} onClick={printPutAwayOrder}>
        {buttonLabel}
      </Button>

      <Box
        ref={printedComponentRef}
        sx={{
          display: "none",
          "@media print": {
            display: "block",
            position: "static",
            overflow: "visible",
            height: "auto",
            width: "100%",
            padding: "24px 16px",
          },
        }}
      >
        <Box
          sx={{
            marginBottom: "20px",
            padding: "16px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        >
          <Typography variant='h6' sx={{ marginBottom: "8px" }}>
            Goods Receipt {goodsReceipt?.agrNumber}
          </Typography>
          <Typography variant='body1'>Work date: {formatDate(goodsReceipt?.checkedInOn, DATE_FORMAT.US)}</Typography>
          <Typography variant='body1'>
            Warehouse location: {formatLocationAddress(goodsReceipt?.warehouseArea?.location, AddressFormat.Shipping)}
          </Typography>
        </Box>
        <PutAwayOrderPalletsTable pallets={pallets} />
      </Box>
    </>
  )
}
